import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { LayoutGrid, ListOrdered, X } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { PageHero, CatIcon } from "@/components/common";
import ActivityCard, { ActivityRow } from "@/components/ActivityCard";
import useTitle from "@/lib/useTitle";
import { dayOf, fmtDay } from "@/lib/time";

const PERIODS = { manha: ["Manhã (até 13h)", "00:00", "13:00"], tarde: ["Tarde (13h–18h)", "13:00", "18:00"], noite: ["Fim de tarde (após 18h)", "18:00", "24:00"] };
const sel = "h-12 w-full rounded-xl border border-black/15 bg-white px-3 text-sm font-medium";

export default function Programacao() {
  useTitle("Programação");
  const site = useSite();
  const { data } = site;
  const [params, setParams] = useSearchParams();
  const get = (k) => params.get(k) || "";
  const set = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p, { replace: true }); };

  const days = [...new Set(data.activities.map((a) => dayOf(a.start)))].sort();
  const day = get("dia") || (days.includes(data.edition.fair_date) ? data.edition.fair_date : days[0]);
  const view = get("vista") || "cartoes";
  const audiences = [...new Set(data.activities.flatMap((a) => a.audience || []))];
  const access = [...new Set(data.activities.flatMap((a) => a.accessibility || []))];
  const catId = data.categories.find((c) => c.slug === get("categoria"))?.id;

  const list = useMemo(() => data.activities.filter((a) => {
    if (day !== "todos" && dayOf(a.start) !== day) return false;
    if (catId && a.category_id !== catId) return false;
    if (get("local") && a.space_id !== get("local")) return false;
    if (get("org") && !(a.organization_ids || []).includes(get("org"))) return false;
    if (get("publico") && !(a.audience || []).includes(get("publico"))) return false;
    if (get("acess") && !(a.accessibility || []).includes(get("acess"))) return false;
    const p = PERIODS[get("hora")];
    if (p) { const t = a.start.slice(11, 16); if (t < p[1] || t >= p[2]) return false; }
    return true;
  }), [data.activities, day, catId, params]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = ["categoria", "local", "org", "publico", "acess", "hora"].filter((k) => get(k));
  const byHour = list.reduce((acc, a) => { const h = a.start.slice(0, 13); (acc[h] = acc[h] || []).push(a); return acc; }, {});

  return (
    <>
      <PageHero eyebrow={`Edição ${data.edition.year}`} title="Programação" tone="#c2410c" image="/img/dance.jpg">Descubra as atividades por dia, hora, tema, local, organização, público e acessibilidade. Guarde as suas favoritas em A minha Feira.</PageHero>
      <div className="container-x py-10">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2" role="tablist" aria-label="Dia">
          {[data.edition.fair_date, "todos"].map((d) => (
            <button key={d} role="tab" aria-selected={day === d} onClick={() => set("dia", d)} data-testid={`filter-day-${d}`}
              className={`shrink-0 w-[72px] h-[78px] flex flex-col items-center justify-center border font-cond uppercase leading-none transition-colors ${day === d ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white text-[var(--ink)] hover:bg-slate-50"}`}>
              {d === "todos" ? <span className="text-sm font-bold">Todos<br />os dias</span> : <>
                <span className="text-xs font-semibold opacity-70">{fmtDay(d).split(/[-,]/)[0].slice(0, 3)}</span>
                <span className="text-3xl font-bold my-0.5">{d.slice(8)}</span>
                <span className="text-xs font-semibold opacity-70">Out</span>
              </>}
            </button>
          ))}
        </div>

        <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar pb-2" aria-label="Categorias">
          <button onClick={() => set("categoria", "")} aria-pressed={!catId} className={`shrink-0 h-10 px-4 rounded-full text-sm font-semibold border ${!catId ? "bg-[var(--leaf)] text-white" : "bg-white"}`} data-testid="filter-category-all">Todas</button>
          {data.categories.map((c) => (
            <button key={c.id} onClick={() => set("categoria", catId === c.id ? "" : c.slug)} aria-pressed={catId === c.id} data-testid={`filter-category-${c.slug}`}
              className="shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-semibold border transition-colors"
              style={catId === c.id ? { background: c.color, color: "#fff", borderColor: c.color } : { background: "#fff" }}>
              <CatIcon name={c.icon} />{c.name}
            </button>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-2 lg:grid-cols-5 gap-3">
          <label className="sr-only" htmlFor="f-hora">Hora</label>
          <select id="f-hora" className={sel} value={get("hora")} onChange={(e) => set("hora", e.target.value)} data-testid="filter-time"><option value="">Qualquer hora</option>{Object.entries(PERIODS).map(([k, v]) => <option key={k} value={k}>{v[0]}</option>)}</select>
          <label className="sr-only" htmlFor="f-local">Local</label>
          <select id="f-local" className={sel} value={get("local")} onChange={(e) => set("local", e.target.value)} data-testid="filter-space"><option value="">Todos os locais</option>{data.spaces.filter((s) => data.activities.some((a) => a.space_id === s.id)).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
          <label className="sr-only" htmlFor="f-org">Organização</label>
          <select id="f-org" className={sel} value={get("org")} onChange={(e) => set("org", e.target.value)} data-testid="filter-org"><option value="">Todas as organizações</option>{data.organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>
          <label className="sr-only" htmlFor="f-pub">Público-alvo</label>
          <select id="f-pub" className={sel} value={get("publico")} onChange={(e) => set("publico", e.target.value)} data-testid="filter-audience"><option value="">Todos os públicos</option>{audiences.map((a) => <option key={a}>{a}</option>)}</select>
          <label className="sr-only" htmlFor="f-acc">Acessibilidade</label>
          <select id="f-acc" className={`${sel} col-span-2 lg:col-span-1`} value={get("acess")} onChange={(e) => set("acess", e.target.value)} data-testid="filter-accessibility"><option value="">Qualquer acessibilidade</option>{access.map((a) => <option key={a}>{a}</option>)}</select>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-b pb-4">
          <p className="text-sm text-slate-700" aria-live="polite" data-testid="programme-count"><strong>{list.length}</strong> atividade{list.length === 1 ? "" : "s"}
            {active.length > 0 && <button onClick={() => { const p = new URLSearchParams(); if (get("dia")) p.set("dia", get("dia")); setParams(p); }} className="ml-3 inline-flex items-center gap-1 underline" data-testid="filter-clear"><X className="h-3.5 w-3.5" aria-hidden />Limpar filtros</button>}
          </p>
          <div className="inline-flex rounded-full border bg-white p-1" role="group" aria-label="Vista">
            <button onClick={() => set("vista", "")} aria-pressed={view === "cartoes"} className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold ${view === "cartoes" ? "bg-[var(--ink)] text-white" : ""}`} data-testid="view-cards"><LayoutGrid className="h-4 w-4" aria-hidden />Cartões</button>
            <button onClick={() => set("vista", "cronologica")} aria-pressed={view === "cronologica"} className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold ${view === "cronologica" ? "bg-[var(--ink)] text-white" : ""}`} data-testid="view-timeline"><ListOrdered className="h-4 w-4" aria-hidden />Cronológica</button>
          </div>
        </div>

        {list.length === 0 ? (
          <p className="py-20 text-center text-slate-600" data-testid="programme-empty">Nenhuma atividade corresponde aos filtros escolhidos.</p>
        ) : view === "cartoes" ? (
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="programme-cards">{list.map((a) => <ActivityCard key={a.id} act={a} />)}</div>
        ) : (
          <ol className="mt-8 relative border-l-2 border-emerald-200 ml-3 sm:ml-6" data-testid="programme-timeline">
            {Object.entries(byHour).map(([h, acts]) => (
              <li key={h} className="pl-6 sm:pl-10 pb-10 relative">
                <span className="absolute -left-[11px] top-1 h-5 w-5 rounded-full bg-[var(--leaf)] ring-4 ring-white" aria-hidden />
                <p className="font-display text-2xl font-black mb-4">{day === "todos" ? `${fmtDay(h.slice(0, 10)).split(",")[0]} · ` : ""}{h.slice(11)}h</p>
                <div className="grid gap-3">{acts.map((a) => <ActivityRow key={a.id} act={a} />)}</div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </>
  );
}
